import { FC, Fragment, useState } from 'react';
import {
	Button,
	Divider,
	Space,
	Tabs,
	Table,
	Typography,
	Alert,
	Spin,
} from 'antd';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import {
	OCRAnalysisResult,
	rawAccountData,
	OCRAnalysisResultAccount,
	OCRAnalysisResultFraudIndicator,
	mapRawToOCRAnalysisResult,
	EODBalanceSheet,
	mapRawToOCRAccount,
	mapRawToOCRFraudIndicator,
	getSessionResult,
	downloadFile,
} from '@/libs/fleet-api/services/ocr';
import { useApp } from '@/App';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getSessionDetail } from '@/libs/fleet-api/services/ocr';
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import { formatCurrency } from '@/libs/antd/formater';

const OCRPageDetail: FC<{
	ocrId: number;
	customerId: string;
	sessionId: string;
	sessionName: string;
}> = ({ ocrId, customerId, sessionId }) => {
	console.log(ocrId);
	const { navigate } = useApp();
	const [activeKey, setActiveKey] = useState('1');
	const submitDownloadFileMutation = useMutation({
		mutationFn: downloadFile,
	});

	// Fetching Session Detail Data
	const { data, isFetching } = useQuery({
		queryKey: ['getSessionDetail', sessionId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getSessionDetail(id);
		},
	});
	console.log('ini dia datanya', data);

	// If result still empty then hit Get Session Result
	const qFetchResult = useQuery({
		queryKey: ['getSessionResult', ocrId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getSessionResult(id);
		},
		enabled: data?.data.analysisResult === '', // Ensure the query only runs when the condition is met
	});
	const resultIsFetching = qFetchResult.isFetching;
	console.log('ini nih', qFetchResult.data);

	// Show loading animation while fetching data
	if (isFetching || resultIsFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	// Set base data between these api results
	const analysisResult = data?.data.analysisResult;
	const result = qFetchResult.data?.data.result;
	const dataDummy = analysisResult
		? JSON.parse(analysisResult)
		: result
			? JSON.parse(result)
			: {};
	console.log(dataDummy);

	// If both api results still processing then show info alert instead
	const isRenderInfoFrag = dataDummy.detail ? true : false;

	const AnalysisFrag = (props: { accountData: rawAccountData }) => {
		// Account Information
		const accountModel: OCRAnalysisResultAccount = mapRawToOCRAccount(
			props.accountData,
		);
		const accountNumber = accountModel.accountNumber;
		const accountSummary: DataRowItem[] = [
			[
				['Account Number', accountModel.accountNumber],
				['Account Holder Number', accountModel.accountHolderName],
			],
			[
				['Address', accountModel.address],
				['Bank Name', accountModel.bankName],
			],
			[
				['Period', accountModel.period],
				[
					'Hasil OCR',
					<>
						<a
							onClick={async (e) => {
								e.preventDefault();
								const { data } =
									await submitDownloadFileMutation.mutateAsync(ocrId);

								if (data && data.file && data.file.url) {
									const link = document.createElement('a');
									link.href = data.file.url;
									link.download = ''; // This will trigger the download
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
								}
							}}
						>
							<DownloadOutlined />
						</a>
					</>,
				],
			],
		];

		// Analysis Results
		const convertAnalysisPivot = (data: OCRAnalysisResult[]) => {
			// Extract unique field names excluding "index"
			const fieldNames: string[] = Object.keys(data[0]).filter(
				(key) => key !== 'index' && key !== 'month',
			);
			// Create the pivot table
			const pivotTable = fieldNames.map((field: string) => {
				const row: { field: string; [key: string]: string | number } = {
					field,
				};

				let total = 0;
				data.forEach((record) => {
					// @ts-expect-error strong typed rule
					const value = record[field] || 0;
					row[record.month] = value; // Use "month" as the pivot column
					total += typeof value === 'number' ? value : 0;
				});
				row['Total'] = total;

				return row;
			});

			return pivotTable;
		};

		// const firstKey = Object.keys(dataDummy.Analysis)[0];
		const analysisResultRaws = dataDummy.Analysis[accountNumber];
		const analysisResults = analysisResultRaws.map(mapRawToOCRAnalysisResult);
		const molohok = convertAnalysisPivot(analysisResults);
		console.log('molohok', molohok);

		const months = Object.keys(molohok[0]).filter((key) => key !== 'field');
		console.log(months);
		// Prepare the columns for the Ant Design table dynamically
		const columns = [
			{
				title: `Monthwise Analysis: ${accountNumber}`,
				dataIndex: 'field',
				key: 'field',
				width: 300,
				render: (value: string) => {
					return (
						<strong style={{ color: '#1557BC' }}>
							{value
								.replace(/_/g, ' ') // Replace underscores with spaces
								.replace(/(?:^|\s)([a-z])/g, (match) =>
									match.toUpperCase(),
								)}{' '}
							{/* Capitalize the first letter of each word */}
						</strong>
					); // Normalize the value before rendering
				},
				onHeaderCell: () => ({
					style: {
						backgroundColor: '#E1EDFF',
					},
				}),
			},
			...months.map((month) => ({
				title: month,
				dataIndex: month,
				key: month,
				render: (value: number) => formatCurrency(value), // Format the column value using formatCurrency
			})),
		];
		console.log(columns);

		return (
			<Fragment>
				<Typography.Title level={3}>Account Summary</Typography.Title>

				{/* Header */}
				<Spin spinning={submitDownloadFileMutation.isPending}>
					<DataRow items={accountSummary} />
				</Spin>

				<Divider />

				{/* Monthwise Analysis */}
				<Table
					bordered
					pagination={false}
					scroll={{ x: 'max-content' }}
					dataSource={
						molohok?.map((item, i) => {
							return {
								...item,
								key: i,
							};
						}) ?? []
					}
					columns={columns}
				/>

				<Divider />
			</Fragment>
		);
	};

	const FraudFrag = () => {
		// Fraud Indicators
		const fraudIndicatorModel: OCRAnalysisResultFraudIndicator[] =
			dataDummy.fraud_indicators.map(mapRawToOCRFraudIndicator);

		return (
			<>
				{/* Fraud Indicators */}
				<Table
					bordered
					pagination={false}
					scroll={{ x: 'max-content' }}
					dataSource={
						fraudIndicatorModel?.map((item, i) => {
							return {
								...item,
								key: i,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Possible Fraud Indicators',
							width: 300,
							render: (_, record) => (
								<strong style={{ color: '#1557BC' }}>
									{record.fraudIndicator}
								</strong>
							),
							onHeaderCell: () => ({
								style: {
									backgroundColor: '#E1EDFF',
								},
							}),
						},
						{
							title: 'Identified',
							render: (_, record) =>
								record.identified.toLowerCase() === 'yes'
									? 'Verified'
									: 'Not Verified',
						},
						{
							title: 'Remarks',
							render: (_, record) =>
								record.remarks.length > 100
									? `${record.remarks.substring(0, 97)}...`
									: record.remarks,
						},
						{
							title: 'Tamper Tag',
							render: (_, record) => record.fraudTag,
						},
					]}
				/>
			</>
		);
	};

	const AnalysisTab = () => {
		return (
			<>
				{/* Account Infos & Analysis Results */}
				{dataDummy.Account.map((accountData: rawAccountData, i: number) => (
					<AnalysisFrag
						key={i}
						accountData={accountData}
					/>
				))}

				{/* Fraud Indicators Table */}
				<FraudFrag />

				{/* Navigation Buttons */}
				<Space
					style={{
						marginTop: 20,
						justifyContent: 'flex-end',
						float: 'right',
					}}
				>
					<Button
						htmlType="button"
						style={{
							width: 200,
						}}
						onClick={(e) => {
							e.preventDefault();
							navigate('OCRPage', {
								customerId: customerId,
							});
						}}
					>
						BACK
					</Button>
					<Button
						type="primary"
						htmlType="button"
						style={{
							width: 200,
							marginLeft: 10, // Adjusted margin to be less
						}}
						onClick={(e) => {
							e.preventDefault();
							setActiveKey('2');
						}}
					>
						NEXT
					</Button>
				</Space>
			</>
		);
	};

	const EODFrag = (props: {
		accountNumber: string;
		eodRaw: EODBalanceSheet;
	}) => {
		const eodBalanceSheetRaw = props.eodRaw;
		const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
		const months = Object.keys(eodBalanceSheetRaw);

		const columns = [
			{
				title: `${props.accountNumber}: Day/Month`,
				dataIndex: 'day',
				key: 'day',
				width: 150,
			},
			...months.map((month) => ({
				title: month.charAt(0).toUpperCase() + month.slice(1), // Capitalize the first letter
				dataIndex: month,
				key: month,
				render: (value: number) => (value ? formatCurrency(value) : '-'),
			})),
		];

		const dataSource = days.map((day) => {
			// @ts-expect-error strong typed rule
			const row: { key: string; day: string; [key: string]: number | null } = {
				key: day,
				day,
			};
			months.forEach((month) => {
				row[month] = eodBalanceSheetRaw[month][day] ?? null;
			});
			return row;
		});

		return (
			<>
				<Table
					bordered
					pagination={false}
					scroll={{ x: 'max-content' }}
					dataSource={dataSource}
					columns={columns}
				/>

				<Divider />
			</>
		);
	};

	const EODTab = () => {
		const eods = dataDummy.balances?.eod_balances ?? dataDummy.eod_balances;

		return (
			<Fragment>
				{dataDummy.Account.map((accountData: rawAccountData, i: number) => (
					<EODFrag
						key={i}
						accountNumber={accountData['Account Number']}
						eodRaw={eods[accountData['Account Number']]}
					/>
				))}

				<Space
					style={{ marginTop: 20, justifyContent: 'flex-end', float: 'right' }}
				>
					<Button
						htmlType="button"
						style={{
							width: 200,
						}}
						onClick={(e) => {
							e.preventDefault();
							setActiveKey('1');
						}}
					>
						BACK
					</Button>
					<Button
						type="primary"
						htmlType="button"
						style={{
							width: 200,
							marginLeft: 10, // Adjusted margin to be less
						}}
						onClick={(e) => {
							e.preventDefault();
							navigate('OCRPage', {
								customerId: customerId,
							});
						}}
					>
						DASHBOARD
					</Button>
				</Space>
			</Fragment>
		);
	};

	return (
		<>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<div
					style={{
						display: 'flex',
						gap: 10,
					}}
				>
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => {
								navigate('OCRPage', { customerId });
							}}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
					<div style={{ flex: 1 }}>
						<FleetTitle>OCR Rekening</FleetTitle>
					</div>
				</div>

				{isRenderInfoFrag ? (
					<Alert
						message={dataDummy.detail}
						type="info"
						showIcon
					/>
				) : (
					<Tabs
						activeKey={activeKey}
						onChange={setActiveKey}
						items={[
							{
								key: '1',
								label: 'Analysis',
								children: AnalysisTab(),
							},
							{
								key: '2',
								label: 'EOD Balance Sheet',
								children: EODTab(),
							},
						]}
					/>
				)}
			</Space>
		</>
	);
};

export default OCRPageDetail;
